import PushNotification from 'react-native-push-notification'
import { eventChannel } from 'redux-saga'
import { put } from 'redux-saga/effects'
import { call, delay, select, spawn, take } from 'typed-redux-saga'
import type { MemberEpisodesResponse } from '@dialogue/services/dist/silkroad/model'

import { showToast } from 'APP/Converse/Toast/helpers'

import { logger } from 'APP/Lib/Datadog'
import { isChannelInconsistent, isPostWhichTriggersNotifications } from 'APP/Lib/MessagingHelpers'
import type { NormalizedPost, NormalizedPostListOrError } from 'APP/Lib/MessagingHelpers/types'
import i18n from 'APP/I18n'
import MattermostSocket, {
  type MattermostSocketInstance,
} from 'APP/Services/Mattermost/MattermostSocket'
import type { RawPost } from 'APP/Services/Mattermost/types'
import type { SilkroadClient } from 'APP/Services/Silkroad'

import { navigationRef as Nav } from 'APP/Nav'
import {
  CONVERSATION_ROUTES,
  CURRENT_CHANNEL_POLL_ROUTES,
  NOTIFICATION_BLACKLIST_ROUTES,
} from 'APP/Nav/Constants'

import { selectAccessToken } from 'APP/Redux/LoginRedux'
import PatientHistoryActions from 'APP/Redux/PatientHistoryRedux'
import { selectIsAppActive, setIsConnectedIfDisconnected } from 'APP/Sagas/AppSessionSagas'
import { selectFeatures } from 'APP/Sagas/FeaturesSaga'
import { selectAppSessionState } from 'APP/Store/AppSession/selectors'
import type { RootState } from 'APP/Store/CreateStore'
import { selectFamilyState } from 'APP/Store/Family/selectors'
import { familyActions } from 'APP/Store/Family/slice'
import { selectHistoryState } from 'APP/Store/History/selectors'
import type { ConversationEpisode } from 'APP/Services/Silkroad/types'

export const loggedInState = (state: RootState) => state.login && state.login.accessToken

export const toLegacyEpisodes = (response: MemberEpisodesResponse): ConversationEpisode[] =>
  // @ts-expect-error is_archivable is present on both models, but @dialogue/services doesn't include it
  response.items as ConversationEpisode[]

export const getConversationsPage = (silkroad: SilkroadClient) =>
  call<Parameters<SilkroadClient['getMyConversations']>, SilkroadClient['getMyConversations']>(
    silkroad.getMyConversations
  )

export const getLegacyEpisodes = (silkroad: SilkroadClient, memberId: string) =>
  call<Parameters<SilkroadClient['getMemberEpisodes']>, SilkroadClient['getMemberEpisodes']>(
    silkroad.getMemberEpisodes,
    memberId
  )

// FIXME: this function should only accept NormalizedPost
// It sometimes is currently receiving a NormalizedPostListOrError object, hence the union
// Once fixed, update:
// - the SetInAppNotificationDataPayload model to require channel ID and post ID
// - the args type for isPostWhichTriggersNotifications to only accept NormalizedPost
export function* inAppNotificationHandler(post: NormalizedPost | NormalizedPostListOrError) {
  try {
    const { inAppNotificationPostId, currentChannelId } = yield* select(selectHistoryState)
    const curRouteName = Nav.getCurrentRoute()?.name
    // @ts-expect-error FIXME: this is not valid when curRouteName is undefined
    const onNotifyingScene = NOTIFICATION_BLACKLIST_ROUTES.indexOf(curRouteName) === -1
    // @ts-expect-error FIXME: this is not valid when curRouteName is undefined
    const onConversationScene = CONVERSATION_ROUTES.indexOf(curRouteName) !== -1
    const onConversationOfPost =
      onConversationScene && 'channel_id' in post && post.channel_id === currentChannelId
    // @ts-expect-error post.id is not defined when we receive a NormalizedPostListOrError object
    // Accessing it here is not dangerous though, so we can simply remove this comment once
    // we address the FIXME above
    const neverNotifiedBefore = inAppNotificationPostId !== post.id
    if (
      onNotifyingScene &&
      !onConversationOfPost &&
      neverNotifiedBefore &&
      isPostWhichTriggersNotifications(post)
    ) {
      // @ts-expect-error These properties are not defined when we receive a NormalizedPostListOrError object
      // This block is guarded by conditions that ensure we actually have these properties though, so we can
      // simply remove this comment once we address the FIXME above
      yield put(PatientHistoryActions.setInAppNotificationData(post.channel_id, post.id))
    }
  } catch (err) {
    logger.debug(
      'inAppNotificationHandler saga error',
      // @ts-expect-error These properties are not defined when we receive a NormalizedPostListOrError object
      // Accessing them here is not dangerous though, so we can simply remove this comment once
      // we address the FIXME above
      { postChannelId: post.channel_id, postId: post.id },
      err
    )
  }
}

export function* refreshUnreadChannels() {
  const { orderedEpisodes, channels } = yield* select(selectHistoryState)
  let totalUnreadMessages = 0

  const unreadChannels = orderedEpisodes.filter((episodeId) => {
    const channel = channels[episodeId]
    const lastPost = channel.order.length && channel.posts[channel.order[0]]
    const { posts, lastViewedAt } = channel
    const postIds = Object.keys(posts)

    const numUnreadMessages = postIds.filter((postId) => {
      const post = posts[postId]
      return post.create_at > lastViewedAt
    }).length
    totalUnreadMessages += numUnreadMessages
    // @ts-expect-error // FIXME: lastPost is the literal `0` when the order is an empty array, ensure we either return a post or `undefined`
    return channel.lastViewedAt < lastPost.create_at
  })

  // @ts-expect-error FIXME: comparison of a boolean and a number is nonsensical, the !! already gives us the right result here so the comparison is not needed
  const weHaveUnreadChannels = !!unreadChannels.length > 0
  PushNotification.setApplicationIconBadgeNumber(totalUnreadMessages)
  yield put(PatientHistoryActions.setHasUnreadChannels(weHaveUnreadChannels))
}

// FIXME: this function should only accept NormalizedPost
// It sometimes is currently receiving a NormalizedPostListOrError object, hence the union
// Once fixed, narrow the args type for inAppNotificationHandler
export function* handleNewMessage(post: NormalizedPost | NormalizedPostListOrError) {
  try {
    const isActive = yield* select(selectIsAppActive)
    if (isActive) {
      yield* spawn(inAppNotificationHandler, post)
      yield* spawn(refreshUnreadChannels)
    }
  } catch (err) {
    logger.debug(
      'handleNewMessage saga error',
      // @ts-expect-error These properties are not defined when we receive a NormalizedPostListOrError object
      // Accessing them here is not dangerous though, so we can simply remove this comment once
      // we address the FIXME above
      { postChannelId: post.channel_id, postId: post.id },
      err
    )
  }
}

export function* channelsConsistencyCheck() {
  const history = yield* select(selectHistoryState)
  const channelIds = Object.keys(history.channels)
  const inconsistentChannelIds = channelIds.filter((id) =>
    isChannelInconsistent(history.channels[id])
  )
  if (inconsistentChannelIds.length > 0) {
    yield put(PatientHistoryActions.resetChannels(inconsistentChannelIds))
  }
}

export function* handleChannelArchiveEvent(channelId: string) {
  try {
    const { currentChannelId, internalNavigationEpisodeId } = yield* select(selectHistoryState)
    const { isAddingAdultMember } = yield* select(selectFamilyState)

    const curRouteName = Nav.getCurrentRoute()?.name
    const onConversationScene = curRouteName && CONVERSATION_ROUTES.indexOf(curRouteName) !== -1
    if (onConversationScene && !internalNavigationEpisodeId && currentChannelId === channelId) {
      yield* call(Nav.goBack)
      yield* delay(500)
      if (isAddingAdultMember) {
        yield put(familyActions.finishAdultInviteFlow())
      } else {
        yield* call(showToast, { title: i18n.t('Intake.toast.success'), type: 'success' })
      }
    } else {
      yield put(PatientHistoryActions.setInternalNavigationEpisodeId(null))
    }
  } catch (err) {
    logger.debug('handleChannelArchiveEvent saga error', { channelId }, err)
  }
}

export function* broadcastMessageHandler(channelId: string, post: RawPost | Partial<RawPost> = {}) {
  try {
    const { dialogue_type, state } = post?.props || {}
    if (dialogue_type === 'episode_state_changed') {
      yield put(PatientHistoryActions.updateChannelState(channelId, state))
      if (state === 'archived') {
        yield* call(handleChannelArchiveEvent, channelId)
      }
    }
  } catch (err) {
    logger.debug(
      'broadcastMessageHandler saga error',
      { channelId, postChannelId: post.channel_id, postId: post.id },
      err
    )
  }
}

/**
 * Long-running saga that manages a WebSocket connection to Mattermost.
 */
export function* mattermostWebsocket() {
  logger.info('mattermostWebsocket saga started')

  const accessToken = yield* select(selectAccessToken)
  // @ts-expect-error FIXME: raise an error if we have no valid access token here
  const mmSocket = MattermostSocket.create(accessToken)
  try {
    const channel = mattermostWebsocketChannel(mmSocket)
    while (true) {
      const event = yield* take(channel)
      if (event) {
        switch (true) {
          case !!event.data:
            yield put(PatientHistoryActions.receiveEvent(event))
            break
          case event.type === 'open':
            yield put(PatientHistoryActions.mattermostSocketReady())
            yield* spawn(setIsConnectedIfDisconnected)
            break
          case event.type === 'close':
            yield put(PatientHistoryActions.mattermostSocketClose())
            break
          case event.type === 'error':
            yield put(PatientHistoryActions.mattermostSocketError())
            break
          default:
            break
        }
      }
    }
  } catch (err) {
    logger.warn('mattermostWebsocket failed', {}, err)
  } finally {
    logger.info('mattermostWebsocket saga ended')
    if (mmSocket && !mmSocket.destroyed) mmSocket.destroy()
    yield put(PatientHistoryActions.mattermostSocketDestroyed())
  }
}

const mattermostWebsocketChannel = (socket: MattermostSocketInstance) => {
  const events = ['open', 'message', 'error', 'close', 'channel_viewed']
  // TODO: provide concrete type for the events here
  return eventChannel<Record<string, unknown>>((emit) => {
    events.forEach((event) => socket.addListener(event, emit))
    return () => events.forEach((event) => socket.removeListener(event, emit))
  })
}

export function* mattermostLongPoll() {
  logger.info('mattermostLongPoll saga started')
  let i = 0
  try {
    yield put(PatientHistoryActions.setMattermostLongPollRunning(true))
    while (true) {
      const appSession = yield* select(selectAppSessionState)
      const { channels, currentChannelId, mmSocketNotReady } = yield* select(selectHistoryState)
      const channel = currentChannelId ? channels?.[currentChannelId] : undefined
      // @ts-expect-error FIXME: nothing seems to ever set a `submitting` property on channel.draft objects
      // Investigate & cleanup if unused
      const submitting = channel && channel.draft && channel.draft.submitting

      const { mattermostPollingConfig } = yield* select(selectFeatures)
      // Interval between loop iterations is the polling frequency for the current channel
      const secondsBetweenLoopIterations = mattermostPollingConfig.currentChannel.default
      // Then we need to divide the fetch-all-channels intervals by that value
      // Example: if we only run the loop every 2s, and we want to fetch all channels every 30s,
      // then we want to fetch the channels on every 15th iteration of the loop, i.e. we calculate 30s / 2s = 15.
      // This must be an integer, so we use Math.ceil - if we end up with a float, fastCheck will never be used.
      const fastCheckPollOnEvery = Math.ceil(
        mattermostPollingConfig.allChannels.fastCheck / secondsBetweenLoopIterations
      )
      const defaultPollOnEvery = Math.ceil(
        mattermostPollingConfig.allChannels.default / secondsBetweenLoopIterations
      )

      if (!submitting) {
        ++i
        const fastCheck = mmSocketNotReady || !appSession.isConnected

        const shouldFetchChannels =
          i % (fastCheck ? fastCheckPollOnEvery : defaultPollOnEvery) === 0
        const curRouteName = Nav.getCurrentRoute()?.name
        const shouldFetchCurrentChannel =
          !!curRouteName &&
          currentChannelId &&
          /* istanbul ignore next */
          (CURRENT_CHANNEL_POLL_ROUTES as readonly string[]).indexOf(curRouteName) !== -1

        if (fastCheck) {
          logger.info('mattermostLongPoll running with fastCheck enabled', {
            appIsOnline: appSession.isConnected,
            defaultPollOnEvery,
            fastCheckPollOnEvery,
            loopIndex: i,
            mattermostPollingConfig,
            mmSocketNotReady,
            secondsBetweenLoopIterations,
            shouldFetchChannels,
            shouldFetchCurrentChannel,
          })
        }

        if (shouldFetchChannels) {
          yield put(PatientHistoryActions.fetchChannelsRequest(undefined))
        } else if (shouldFetchCurrentChannel) {
          yield put(PatientHistoryActions.fetchNewPostsForChannel(currentChannelId, true))
        }
      }
      yield* delay(secondsBetweenLoopIterations * 1000)
    }
  } catch (error) {
    logger.warn('mattermostLongPoll failed', {}, error)
  } finally {
    logger.info('mattermostLongPoll saga ended')
    yield put(PatientHistoryActions.setMattermostLongPollRunning(false))
  }
}
